import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Trophy,
  Briefcase,
  MapPin,
  Plane,
  Waves,
} from "lucide-react";
import Seo from "@/components/Seo";
import { DESTINATION_IMAGES } from "@/lib/media";

const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Thailand DMC", path: "/thailand-dmc" },
];

const SERVICES = [
  {
    icon: Trophy,
    title: "MICE & Conferences",
    copy:
      "Full conference production at QSNCC, IMPACT Muang Thong Thani, BITEC and ICC Bangkok — from 300-pax workshops to 5,000-pax dealer congresses.",
  },
  {
    icon: Briefcase,
    title: "Corporate Group Travel",
    copy:
      "Bangkok corporate retreats, hotel buyouts, F&B coordination, transfer logistics and dedicated English-speaking account management.",
  },
  {
    icon: Trophy,
    title: "Incentive Travel",
    copy:
      "Themed gala dinners on rooftop bars, beachfront welcome parties in Phuket, elephant sanctuary experiences in Chiang Mai, and private yacht charters.",
  },
  {
    icon: Waves,
    title: "Leisure & Multi-City",
    copy:
      "Curated multi-city itineraries — Bangkok culture to Phuket beach to Chiang Mai temples — wrapped with private guides, hand-picked stays, and seamless transfers.",
  },
];

const CITIES = [
  {
    name: "Bangkok",
    note:
      "Asia's most-visited city. Five-star inventory across Sukhumvit, Sathorn and the riverside Chao Phraya. World-class MICE venues, mature transport, and unbeatable F&B make Bangkok the workhorse of any Thailand programme.",
  },
  {
    name: "Phuket",
    note:
      "Beach incentive heaven. Banyan Tree, Anantara, Trisara, Aman and JW Marriott offer luxury buyouts. Direct international flights and yacht-based experiences elevate every programme.",
  },
  {
    name: "Chiang Mai",
    note:
      "Northern Thailand's cultural capital. Forest temples, elephant sanctuaries, hill-tribe villages and boutique resorts make Chiang Mai the unexpected, photogenic alternative to beach destinations.",
  },
];

const VENUES = [
  { name: "Queen Sirikit National Convention Centre (QSNCC)", note: "Bangkok's largest urban convention venue — 280,000 sqm post-renovation" },
  { name: "IMPACT Muang Thong Thani", note: "Asia's largest exhibition complex at 140,000 sqm" },
  { name: "BITEC Bangna", note: "70,000 sqm exhibition halls, MRT-connected" },
  { name: "Centara Grand at CentralWorld", note: "Bangkok's tallest convention hotel — direct mall access" },
  { name: "Banyan Tree Phuket", note: "Lagoon resort with private pool villas — top incentive choice" },
  { name: "Four Seasons Chiang Mai", note: "Rice-paddy retreat for executive offsites and small incentives" },
];

const FAQS = [
  {
    q: "Why is Thailand a top choice for incentive travel?",
    a: "Thailand combines three things almost no other destination offers together — world-class beach resorts at competitive rates, deep cultural and culinary heritage that creates memorable moments, and mature MICE infrastructure that handles 2,000-pax groups effortlessly. For incentive programmes that need to feel both luxurious and unique, Thailand consistently outperforms.",
  },
  {
    q: "Can Trawelldmc manage a multi-city Thailand programme — Bangkok, Phuket and Chiang Mai?",
    a: "Yes — multi-city Thailand is one of our most-requested formats. We handle internal flight blocks (Bangkok Airways and Thai AirAsia have reliable routings), hotel-to-hotel handovers, English-speaking guides at each destination, and one named programme manager who travels with the group end-to-end to ensure consistency.",
  },
  {
    q: "What is the lead time for a Thailand MICE event?",
    a: "For events of 1,000+ pax at IMPACT or QSNCC, we recommend 9–12 months. For Phuket beach incentives at top-tier properties like Trisara or Aman, 6–9 months is ideal since hotels often need a full buyout. For smaller corporate groups of 50–150 pax, 4–6 weeks of lead time is usually workable.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function ThailandDmc() {
  return (
    <div data-testid="thailand-dmc-page">
      <Seo
        path="/thailand-dmc"
        title="DMC Thailand — MICE & Incentive Travel | Trawelldmc"
        description="Expert DMC in Thailand. Bangkok, Phuket & Chiang Mai MICE, incentive and corporate travel. Trusted by 140,000+ travellers. Request a quote."
        breadcrumb={BREADCRUMB}
        extraJsonLd={FAQ_JSONLD}
      />

      <section className="relative">
        <img
          src={DESTINATION_IMAGES.thailand}
          alt="Bangkok incentive travel — Trawelldmc DMC Thailand"
          width="1920"
          height="1080"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(11,25,44,0.92) 0%, rgba(11,25,44,0.55) 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-32 md:py-44 text-white">
          <div className="max-w-3xl">
            <div className="overline !text-[#FF5238] mb-5">
              Thailand DMC · Bangkok · Phuket · Chiang Mai
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.06]">
              <span className="text-[#FF5238]">Thailand</span> Destination Management Company (DMC) & MICE Specialist
            </h1>
            <p className="text-white/80 mt-8 text-lg max-w-2xl leading-relaxed">
              MICE, incentive and corporate travel across Thailand's three
              destination icons — engineered end-to-end by a team with two
              decades on the ground.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/inquiry?destination=Thailand"
                className="btn-primary"
                data-testid="th-cta-proposal"
              >
                Get a Thailand proposal <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/quote"
                className="btn-secondary"
                data-testid="th-cta-ai-quote"
              >
                Instant AI estimate
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHY THAILAND */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Why Thailand</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              The world's most <br />versatile MICE destination.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-slate-700 text-lg leading-relaxed">
            <p>
              Thailand is the most versatile MICE destination in Southeast
              Asia. Few countries offer such a wide spectrum of programme
              storylines within a single visa, a single language, and a
              single account team. A 1,500-pax dealer conference in Bangkok
              one week. A 200-pax beach incentive in Phuket the next. A
              50-pax leadership offsite in Chiang Mai's mountains. Trawelldmc
              delivers all three from one operations centre.
            </p>
            <p>
              Bangkok is the workhorse. The Queen Sirikit National Convention
              Centre — recently expanded to 280,000 sqm — sits inside one of
              Asia's most efficient cities. IMPACT Muang Thong Thani, BITEC
              Bangna and ICC Bangkok between them can handle anything from a
              10-stand exhibition to a 10,000-pax congress. The CBD's
              concentration of international 5-star hotels (Mandarin Oriental,
              St. Regis, Park Hyatt, Waldorf Astoria, Capella) means inventory
              is never a constraint.
            </p>
            <p>
              Phuket is the incentive jewel. Banyan Tree, Trisara, Anantara,
              Aman, Six Senses and the JW Marriott compete for high-end
              buyouts. Yacht charters, private island transfers, beachfront
              gala dinners and Muay Thai experiences create the kind of
              programme delegates remember years later. Chiang Mai is the
              wildcard — forest retreats, elephant sanctuaries, temple
              ceremonies and rice-paddy lunches make for content that no
              competitor's incentive trip can replicate.
            </p>
            <p>
              Trawelldmc has operated Thailand for two decades. Our local team
              owns every venue inspection, every transfer, every contract.
              From visa documentation guidance to AV technical riders to
              halal-certified F&B and last-minute weather contingencies, we
              don't outsource what matters.
            </p>
          </div>
        </div>
      </section>

      {/* CITIES */}
      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline mb-4">Where we work</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Our Thailand footprint.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CITIES.map((c) => (
              <div key={c.name} className="card-flat p-8">
                <MapPin className="h-8 w-8 text-[#FF5238]" />
                <h3 className="font-heading text-xl font-medium tracking-tight text-[#0B192C] mt-5">
                  {c.name}
                </h3>
                <p className="text-slate-600 mt-3 leading-relaxed">{c.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">What we deliver</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Our Thailand DMC services.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SERVICES.map((s) => (
            <div key={s.title} className="card-flat p-8">
              <s.icon className="h-8 w-8 text-[#FF5238]" />
              <h3 className="font-heading text-xl font-medium tracking-tight text-[#0B192C] mt-5">
                {s.title}
              </h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{s.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* VENUES */}
      <section className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline !text-[#FF5238] mb-4">Top venues</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
              Hotels & venues <br />
              <span className="text-[#FF5238]">we partner with.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
            {VENUES.map((v) => (
              <div key={v.name} className="border-t border-white/15 pt-5">
                <div className="flex gap-3 items-start">
                  <Building2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-heading text-lg font-medium">
                      {v.name}
                    </div>
                    <div className="text-sm text-white/60 mt-1">{v.note}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">FAQ</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Frequently Asked Questions
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {FAQS.map((f, i) => (
            <div key={f.q} data-testid={`th-faq-${i}`}>
              <h3 className="font-heading text-xl font-medium text-[#0B192C] tracking-tight">
                {f.q}
              </h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Next step</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Ready to plan your <br />
              <span className="text-[#FF5238]">Thailand programme?</span>
            </h2>
            <p className="text-slate-600 mt-6 max-w-xl text-lg leading-relaxed">
              Contact Trawelldmc for a custom Thailand proposal. We respond
              within 24 hours.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Link
              to="/inquiry?destination=Thailand"
              className="btn-primary w-full justify-center"
              data-testid="th-bottom-cta"
            >
              Request a proposal <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              className="btn-secondary w-full justify-center"
            >
              Contact our team
            </Link>
          </div>
        </div>
      </section>

      {/* YOU MAY ALSO LIKE */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16">
          <div className="overline mb-6">You may also like</div>
          <nav
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            aria-label="Related DMC destination pages"
          >
            <Link to="/singapore-dmc" className="group flex items-start gap-3" data-testid="th-link-singapore">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Singapore</div>
                <div className="text-sm text-slate-500 mt-1">MICE capital of Asia</div>
              </div>
            </Link>
            <Link to="/malaysia-dmc" className="group flex items-start gap-3" data-testid="th-link-malaysia">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Malaysia</div>
                <div className="text-sm text-slate-500 mt-1">KL · Penang · Langkawi</div>
              </div>
            </Link>
            <Link to="/dubai-dmc" className="group flex items-start gap-3" data-testid="th-link-dubai">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Dubai</div>
                <div className="text-sm text-slate-500 mt-1">GCC HQ · MICE & corporate</div>
              </div>
            </Link>
            <Link to="/" className="group flex items-start gap-3" data-testid="th-link-home">
              <Plane className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">Trawelldmc Home</div>
                <div className="text-sm text-slate-500 mt-1">All destinations & services</div>
              </div>
            </Link>
          </nav>
        </div>
      </section>
    </div>
  );
}
